/**
 * Feedback Campaigns admin page
 *
 * Phase 1 of the Upward Feedback Campaigns project (May 2026). Surfaces
 * the campaign lifecycle to HR admins:
 *   - List + status filter
 *   - Create draft (audience filter, template, close date)
 *   - Preview audience (cohort sanity-check before any launch)
 *   - Test launch (dry-run to explicit user IDs; bypasses real audience)
 *   - Launch (snapshots audience, fans out requests, queues invites)
 *   - Monitor (anonymised response counts per subject)
 *   - Close / Cancel
 *
 * Visibility for campaign responses is respondent-only (subject can never
 * see who said what); the Monitor view only exposes aggregated counts.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Plus,
  Send,
  TestTube2,
  Eye,
  Users,
  CalendarClock,
  StopCircle,
  XCircle,
  Loader2,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Megaphone,
  Mail,
  ClipboardList,
  Download,
  FileDown,
  Bell,
  BellRing,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Progress } from '@/components/ui/progress'
import feedbackCampaignsService, {
  type FeedbackCampaign,
  type CampaignStatus,
  type SubjectRelationship,
  type AudiencePreview,
  type CampaignProgress,
  type CampaignRecipient,
  type CampaignCreate,
  type CampaignUpdate,
  type CampaignReportJob,
} from '@/services/api/feedbackCampaignsService'
import questionBankService, { type FeedbackTemplate } from '@/services/api/questionBankService'
import { parseApiDate, toDatetimeLocalValue } from '@/lib/apiDate'
import toast from 'react-hot-toast'

// ----------------------------------------------------------------------------
// Status presentation
// ----------------------------------------------------------------------------

const STATUS_CONFIG: Record<CampaignStatus, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-slate-100 text-slate-700' },
  scheduled: { label: 'Scheduled', className: 'bg-amber-100 text-amber-700' },
  launched: { label: 'Launched', className: 'bg-blue-100 text-blue-700' },
  closed: { label: 'Closed', className: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelled', className: 'bg-rose-100 text-rose-700' },
}

const RELATIONSHIP_LABEL: Record<SubjectRelationship, string> = {
  line_manager: 'People leader',
  campus_general_manager: 'Campus General Manager',
  organisation: 'Whole organisation',
  custom: 'Custom',
}

const EMPLOYMENT_TYPES = ['PERM', 'FTC', 'CASUAL', 'IC'] as const

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function fmt(date: string | null | undefined, withTime = false): string {
  if (!date) return '—'
  // parseApiDate, not new Date(): campaign timestamps come back offset-less
  // and are UTC by convention — see @/lib/apiDate.
  const d = parseApiDate(date)
  if (Number.isNaN(d.getTime())) return '—'
  return withTime
    ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : d.toLocaleDateString(undefined, { dateStyle: 'medium' })
}

function toIsoLocal(value: string): string {
  // <input type="datetime-local"> produces a naive string in the viewer's
  // local time; new Date() reads it as such, so toISOString() gives us the
  // correct UTC instant to persist. Inverse of toDatetimeLocalValue().
  if (!value) return ''
  return new Date(value).toISOString()
}

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------

const DEFAULT_CREATE: CampaignCreate = {
  name: '',
  description: '',
  template_id: '',
  close_at: '',
  reminder_lead_days: 7,
  subject_relationship: 'line_manager',
  audience_filter: {
    employment_types: ['PERM', 'FTC'],
    campuses: [],
    excluded_user_ids: [],
    included_user_ids: [],
  },
  test_recipient_user_ids: [],
}

const FeedbackCampaignsPage: React.FC = () => {
  // ---- state
  const [campaigns, setCampaigns] = useState<FeedbackCampaign[]>([])
  const [templates, setTemplates] = useState<FeedbackTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | 'all'>('all')

  // create / edit dialog
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingCampaign, setEditingCampaign] = useState<FeedbackCampaign | null>(null)
  const [form, setForm] = useState<CampaignCreate>(DEFAULT_CREATE)
  const [saving, setSaving] = useState(false)
  // Once launched, the audience and questions are frozen (people already have
  // the survey) — but the deadlines stay editable, because HR routinely
  // extends a live campaign. The editor locks everything else in that mode.
  const isEditingLaunched = editingCampaign?.status === 'launched'

  // detail drawer
  const [drawerCampaignId, setDrawerCampaignId] = useState<string | null>(null)
  const [drawerCampaign, setDrawerCampaign] = useState<FeedbackCampaign | null>(null)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [drawerProgress, setDrawerProgress] = useState<CampaignProgress | null>(null)
  const [drawerRecipients, setDrawerRecipients] = useState<CampaignRecipient[]>([])
  const [drawerTab, setDrawerTab] = useState<'overview' | 'progress' | 'recipients'>('overview')

  // audience preview dialog
  const [previewOpen, setPreviewOpen] = useState(false)
  const [preview, setPreview] = useState<AudiencePreview | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  // test launch dialog
  const [testLaunchOpen, setTestLaunchOpen] = useState(false)
  const [testLaunchIds, setTestLaunchIds] = useState('')
  const [testLaunching, setTestLaunching] = useState(false)

  // export
  const [exporting, setExporting] = useState(false)
  // Background PDF report job for the drawer campaign (null = none
  // visible). Polling interval lives in the ref so unmount/drawer-close
  // can clear it.
  const [reportJob, setReportJob] = useState<CampaignReportJob | null>(null)
  const reportJobTimer = useRef<number | null>(null)

  // manual reminder trigger
  const [sendingReminder, setSendingReminder] = useState(false)

  // reminder preview modal — HR reviews rendered HTML before firing
  const [reminderPreview, setReminderPreview] = useState<{
    variant: 'first' | 'second' | 'final'
    subject: string
    body_html: string
    close_str: string
    campaigns_in_bundle: string[]
    audience: {
      required_only: boolean
      recipient_count: number
      outstanding_total: number
      suppressed: number
      outstanding_by_employment_type: Array<{ label: string; count: number }>
    }
  } | null>(null)
  const [previewingReminder, setPreviewingReminder] = useState(false)
  // Skip the voluntary cohort. The launch comms told independent contractors
  // and casuals their participation was optional, so chasing them is a
  // different act from chasing the people who were asked to take part.
  const [remindRequiredOnly, setRemindRequiredOnly] = useState(false)

  // retry undelivered invites
  const [retryingInvites, setRetryingInvites] = useState(false)

  // bundle launch ("Launch with…")
  const [bundleOpen, setBundleOpen] = useState(false)
  const [bundlePartnerId, setBundlePartnerId] = useState<string>('')
  const [bundleLaunching, setBundleLaunching] = useState(false)
  // Test-launch flow uses the same picker — toggle decides whether to
  // fire the real or the test endpoint. Test mode also collects a
  // comma/newline-separated list of respondent IDs.
  const [bundleMode, setBundleMode] = useState<'live' | 'test'>('live')
  const [bundleTestIds, setBundleTestIds] = useState('')

  // confirm dialogs
  const [confirmAction, setConfirmAction] = useState<
    | { kind: 'launch' | 'close' | 'cancel'; campaignId: string; campaignName: string }
    // Re-firing an already-sent reminder mails every outstanding respondent
    // again, so it goes through the confirm dialog rather than one click.
    | {
        kind: 'resend-reminder'
        campaignId: string
        campaignName: string
        variant: 'first' | 'second' | 'final'
      }
    | null
  >(null)
  const [confirmBusy, setConfirmBusy] = useState(false)

  // ---- effects
  const loadCampaigns = async () => {
    setLoading(true)
    try {
      const data = await feedbackCampaignsService.listCampaigns(
        statusFilter === 'all' ? undefined : statusFilter,
      )
      setCampaigns(data)
    } catch (err: any) {
      console.error('Failed to load campaigns', err)
      toast.error(err?.response?.data?.detail || 'Failed to load campaigns')
    } finally {
      setLoading(false)
    }
  }

  const loadTemplates = async () => {
    try {
      // We only want annual_campaign templates as create options. Keep all
      // active templates in state so we can resolve names for existing
      // campaigns referencing in_the_moment templates from the transition.
      // Backend caps `limit` at 100 (Query ge=1, le=100). 100 templates is plenty
      // headroom for the campaigns dropdown — if it ever overflows we'll paginate.
      const response = await questionBankService.listTemplates({ limit: 100, is_active: true })
      const items = response?.items || (Array.isArray(response) ? response : [])
      setTemplates(items)
    } catch (err) {
      console.error('Failed to load templates', err)
    }
  }

  useEffect(() => {
    loadCampaigns()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter])

  useEffect(() => {
    loadTemplates()
  }, [])

  // ---- derived
  const annualCampaignTemplates = useMemo(
    () => templates.filter((t) => (t.template_type ?? 'in_the_moment') === 'annual_campaign'),
    [templates],
  )

  const templatesById = useMemo(() => {
    const map = new Map<string, FeedbackTemplate>()
    templates.forEach((t) => map.set(t.id, t))
    return map
  }, [templates])

  // ---- create / edit handlers
  const openCreate = () => {
    setEditingCampaign(null)
    setForm({
      ...DEFAULT_CREATE,
      // sensible default: close in 14 days, as the local wall-clock time the
      // datetime-local control reads (UTC digits would be read as local time)
      close_at: toDatetimeLocalValue(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString()),
    })
    setEditorOpen(true)
  }

  const openEdit = (campaign: FeedbackCampaign) => {
    setEditingCampaign(campaign)
    const af = (campaign.audience_filter || {}) as any
    setForm({
      name: campaign.name,
      description: campaign.description || '',
      template_id: campaign.template_id,
      // Convert UTC -> local wall clock; slicing the raw string would put UTC
      // digits into a control the browser reads as local time.
      close_at: toDatetimeLocalValue(campaign.close_at),
      display_close_at: toDatetimeLocalValue(campaign.display_close_at),
      reminder_lead_days: campaign.reminder_lead_days,
      subject_relationship: campaign.subject_relationship,
      audience_filter: {
        employment_types: af.employment_types || ['PERM', 'FTC'],
        campuses: af.campuses || [],
        excluded_user_ids: af.excluded_user_ids || [],
        included_user_ids: af.included_user_ids || [],
      },
      test_recipient_user_ids: campaign.test_recipient_user_ids || [],
      launch_at: campaign.launch_at || undefined,
    })
    setEditorOpen(true)
  }

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error('Name is required')
      return
    }
    if (!form.template_id) {
      toast.error('Template is required')
      return
    }
    if (!form.close_at) {
      toast.error('Close date is required')
      return
    }
    setSaving(true)
    try {
      const deadlines = {
        close_at: toIsoLocal(form.close_at as string),
        // Empty = communicate the real close date; send null so clearing the
        // field actually unsets it server-side rather than being ignored.
        display_close_at: form.display_close_at
          ? toIsoLocal(form.display_close_at as string)
          : null,
        reminder_lead_days: form.reminder_lead_days,
      }
      if (editingCampaign) {
        // A launched campaign only accepts the deadline fields — sending the
        // whole form would 409 on the first frozen field (name).
        const payload: CampaignUpdate = isEditingLaunched
          ? deadlines
          : { ...form, ...deadlines }
        await feedbackCampaignsService.updateCampaign(editingCampaign.id, payload)
        toast.success(isEditingLaunched ? 'Dates updated' : 'Campaign updated')
      } else {
        await feedbackCampaignsService.createCampaign({ ...form, ...deadlines } as CampaignCreate)
        toast.success('Campaign created')
      }
      setEditorOpen(false)
      loadCampaigns()
    } catch (err: any) {
      console.error('Save failed', err)
      const detail = err?.response?.data?.detail
      const msg = typeof detail === 'string' ? detail : 'Failed to save campaign'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  // ---- detail drawer
  const openDrawer = async (campaignId: string) => {
    setDrawerCampaignId(campaignId)
    setDrawerCampaign(null)
    setDrawerProgress(null)
    setDrawerRecipients([])
    setDrawerTab('overview')
    setDrawerLoading(true)
    try {
      const [campaign, progress, recipients] = await Promise.all([
        feedbackCampaignsService.getCampaign(campaignId),
        feedbackCampaignsService.getProgress(campaignId).catch(() => null),
        feedbackCampaignsService.listRecipients(campaignId).catch(() => []),
      ])
      setDrawerCampaign(campaign)
      setDrawerProgress(progress)
      setDrawerRecipients(recipients)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to load campaign')
    } finally {
      setDrawerLoading(false)
    }
  }

  const closeDrawer = () => {
    setDrawerCampaignId(null)
    setDrawerCampaign(null)
    setDrawerProgress(null)
    setDrawerRecipients([])
  }

  // ---- audience preview
  const previewAudience = async (campaignId: string) => {
    setPreviewOpen(true)
    setPreviewLoading(true)
    setPreview(null)
    try {
      const p = await feedbackCampaignsService.previewAudience(campaignId)
      setPreview(p)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to preview audience')
      setPreviewOpen(false)
    } finally {
      setPreviewLoading(false)
    }
  }

  // ---- launch / close / cancel
  const performAction = async () => {
    if (!confirmAction) return
    setConfirmBusy(true)
    try {
      if (confirmAction.kind === 'launch') {
        const summary = await feedbackCampaignsService.launchCampaign(confirmAction.campaignId)
        toast.success(`Launched — ${summary.recipients_created} respondents, ${summary.requests_created} requests`)
      } else if (confirmAction.kind === 'close') {
        await feedbackCampaignsService.closeCampaign(confirmAction.campaignId)
        toast.success('Campaign closed')
      } else if (confirmAction.kind === 'cancel') {
        await feedbackCampaignsService.cancelCampaign(confirmAction.campaignId)
        toast.success('Campaign cancelled')
      } else if (confirmAction.kind === 'resend-reminder') {
        await feedbackCampaignsService.sendCampaignReminder(confirmAction.campaignId, {
          variant: confirmAction.variant,
        })
        toast.success(
          confirmAction.variant === 'final'
            ? 'Final reminder queued — everyone still outstanding will be notified shortly'
            : 'Reminder queued — everyone still outstanding will be notified shortly',
        )
      }
      setConfirmAction(null)
      loadCampaigns()
      if (drawerCampaignId === confirmAction.campaignId) openDrawer(confirmAction.campaignId)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Action failed')
    } finally {
      setConfirmBusy(false)
    }
  }

  // Export — triggers a browser download. We accept the small UX cost of
  // building the file synchronously rather than streaming progress: even
  // a thousand-respondent campaign only takes a couple seconds to render.
  const handleExport = async (campaignId: string, includeTest = false) => {
    setExporting(true)
    try {
      // minN: 1 disables the small-cell suppression (the "—" redaction the
      // server applies when a manager has fewer than 3 responses). HR needs
      // every response visible per manager for analytics. Respondent
      // identities are STILL anonymised in the file (R-001, R-002, …) — this
      // only un-redacts the response *content*, never reveals who said what.
      const { blob, filename } = await feedbackCampaignsService.exportCampaign(campaignId, {
        includeTest,
        minN: 1,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast.success('Export downloaded')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  // ── Background PDF report job ──────────────────────────────────────
  // Generating 200+ per-subject PDFs (one AI synthesis each) takes
  // minutes — far past the axios/ingress timeout chain — so the build
  // runs as a backend job. Generate creates the job, we poll every 2s
  // showing "Building X of Y…", and the ZIP auto-downloads on
  // completion. A page reload resumes the progress display via /latest.

  const stopReportJobPolling = useCallback(() => {
    if (reportJobTimer.current !== null) {
      window.clearInterval(reportJobTimer.current)
      reportJobTimer.current = null
    }
  }, [])

  const downloadReportJobZip = async (campaignId: string, job: CampaignReportJob) => {
    const { blob, filename } = await feedbackCampaignsService.downloadReportJob(
      campaignId,
      job.job_id,
    )
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const pollReportJob = useCallback(
    (campaignId: string, jobId: string, autoDownload: boolean) => {
      stopReportJobPolling()
      reportJobTimer.current = window.setInterval(async () => {
        try {
          const job = await feedbackCampaignsService.getReportJob(campaignId, jobId)
          setReportJob(job)
          if (job.status === 'complete') {
            stopReportJobPolling()
            if (autoDownload) {
              // The download itself gets its own error handling: if it
              // fails (network blip, token refresh, deploy revision swap)
              // the job state is already 'complete', so the button shows
              // "Download PDF reports" for a manual retry — but the user
              // must be TOLD, not left with a silently-missing file.
              try {
                await downloadReportJobZip(campaignId, job)
                toast.success(
                  `PDF reports downloaded — ${job.built_count} report${job.built_count === 1 ? '' : 's'}` +
                    (job.failed_subjects ? ` (${job.failed_subjects} failed, see _FAILED_ files)` : ''),
                )
              } catch {
                toast.error(
                  'The reports are built, but the download failed — click "Download PDF reports" to retry.',
                )
              }
            } else {
              toast.success('PDF reports are ready — click Download PDF reports.')
            }
          } else if (job.status === 'failed') {
            stopReportJobPolling()
            toast.error(job.error || 'PDF report build failed')
          } else if (job.stale) {
            stopReportJobPolling()
            toast.error('PDF build stalled — click Generate to restart it.')
          }
        } catch {
          // Transient poll error (network blip) — keep polling.
        }
      }, 2000)
    },
    [stopReportJobPolling],
  )

  const handleGenerateReports = async (campaignId: string) => {
    try {
      const job = await feedbackCampaignsService.createReportJob(campaignId)
      setReportJob(job)
      if (job.status === 'complete') {
        // Re-clicked on an already-finished job row — just download.
        await downloadReportJobZip(campaignId, job)
        toast.success('PDF reports downloaded')
        return
      }
      toast.success(
        'Building PDF reports in the background — the download starts automatically when ready.',
      )
      pollReportJob(campaignId, job.job_id, true)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not start the PDF report build')
    }
  }

  // Resume a visible build when the drawer (re)opens — page reloads and
  // drawer switches shouldn't lose the progress display of a job that's
  // still running server-side. Resumed jobs don't auto-download (a
  // surprise download on drawer open would be jarring); the button
  // flips to "Download PDF reports" when the build completes.
  //
  // COMPLETED jobs are surfaced too (with the stored ZIP still present):
  // otherwise a reload after a finished build shows "Generate" and a
  // click triggers a needless full rebuild instead of downloading the
  // archive that's already sitting in the database.
  useEffect(() => {
    stopReportJobPolling()
    setReportJob(null)
    if (!drawerCampaignId) return
    let cancelled = false
    feedbackCampaignsService
      .getLatestReportJob(drawerCampaignId)
      .then((job) => {
        if (cancelled || !job) return
        if (job.status === 'pending' || (job.status === 'running' && !job.stale)) {
          setReportJob(job)
          pollReportJob(drawerCampaignId, job.job_id, false)
        } else if (job.status === 'complete' && job.zip_size_bytes) {
          // Finished archive available — flip the button to Download.
          setReportJob(job)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [drawerCampaignId, pollReportJob, stopReportJobPolling])

  // Clear the poll interval on unmount.
  useEffect(() => stopReportJobPolling, [stopReportJobPolling])

  /**
   * Bundle launch — fan out the current drawer campaign together with
   * a sibling draft. Backend sends ONE invite email per respondent
   * covering every survey they qualify for. Each campaign stays
   * independent post-launch (own status, close_at, etc).
   *
   * Test mode uses the same picker but routes to /campaign-bundles/test-launch
   * with an explicit cohort, so HR can validate the consolidated email
   * + both surveys end-to-end without touching the real audience.
   */
  const handleLaunchBundle = async () => {
    if (!drawerCampaignId || !bundlePartnerId) return
    setBundleLaunching(true)
    try {
      if (bundleMode === 'test') {
        const ids = bundleTestIds
          .split(/[\n,;\s]+/)
          .map((s) => s.trim())
          .filter(Boolean)
        if (ids.length === 0) {
          toast.error('Enter at least one user ID for the test cohort')
          return
        }
        const summary = await feedbackCampaignsService.testLaunchCampaignBundle(
          [drawerCampaignId, bundlePartnerId],
          ids,
        )
        toast.success(
          `Test bundle launched — ${summary.invitations_queued} consolidated invite${summary.invitations_queued === 1 ? '' : 's'} queued`,
        )
      } else {
        const summary = await feedbackCampaignsService.launchCampaignBundle([
          drawerCampaignId,
          bundlePartnerId,
        ])
        toast.success(
          `Bundle launched — ${summary.invitations_queued} respondents queued for invite across ${summary.campaign_ids.length} campaigns`,
        )
      }
      setBundleOpen(false)
      setBundlePartnerId('')
      setBundleTestIds('')
      setBundleMode('live')
      loadCampaigns()
      // refresh the drawer so status + linked_campaign_ids show
      openDrawer(drawerCampaignId)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Bundle launch failed')
    } finally {
      setBundleLaunching(false)
    }
  }

  /**
   * Manually fire the next reminder. Backend chooses 'first' if the
   * mid-cycle one hasn't been sent yet, otherwise 'second'. Both
   * reminders only land in non-responders' inboxes (already-submitted
   * respondents are filtered out server-side), and the backend stamps
   * the appropriate timestamp so the scheduled tick won't re-fire.
   */
  /**
   * Retry sending invitations to recipients whose first launch send
   * failed (their recipient row still has `invitation_sent_at` null).
   * Backend auto-detects bundle linkage and consolidates per respondent,
   * and is idempotent — recipients already marked delivered are skipped
   * so we never re-email people who got it the first time.
   */
  const handleRetryUndelivered = async (campaign: FeedbackCampaign) => {
    setRetryingInvites(true)
    try {
      const result = await feedbackCampaignsService.retryUndeliveredInvites(campaign.id)
      if (!result.queued || result.undelivered_count === 0) {
        toast.success('All invites already delivered — nothing to retry.')
      } else {
        toast.success(
          `${result.undelivered_count} undelivered invite${result.undelivered_count === 1 ? '' : 's'} queued for re-send`,
        )
      }
      openDrawer(campaign.id)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to retry undelivered invites')
    } finally {
      setRetryingInvites(false)
    }
  }

  /**
   * Preview the rendered reminder email body before firing.  Reuses the
   * backend's exact build logic (same subject + html the recipient will
   * receive) so HR can sanity-check copy, bundle linkage + buttons,
   * and the close-date wording before committing the manual fire.
   */
  const handlePreviewReminder = async (
    campaign: FeedbackCampaign,
    variant?: 'first' | 'second' | 'final',
    // Passed explicitly rather than read from state: the checkbox's onChange
    // calls this in the same tick as setRemindRequiredOnly, so the state it
    // would read here is still the PRE-tick value. That shipped once — the box
    // was ticked and the count stayed on the unfiltered total.
    requiredOnly: boolean = remindRequiredOnly,
  ) => {
    setPreviewingReminder(true)
    try {
      const result = await feedbackCampaignsService.previewCampaignReminder(
        campaign.id,
        { ...(variant ? { variant } : {}), requiredOnly },
      )
      setReminderPreview({
        variant: result.variant,
        subject: result.subject,
        body_html: result.body_html,
        close_str: result.close_str,
        campaigns_in_bundle: result.campaigns_in_bundle,
        audience: result.audience,
      })
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to render reminder preview')
    } finally {
      setPreviewingReminder(false)
    }
  }

  const handleSendReminderNow = async (
    campaign: FeedbackCampaign,
    variant?: 'first' | 'second' | 'final',
    // Sourced from the audience the SERVER returned for the preview, not from
    // local state, so the number on the button and the filter on the send are
    // the same fact rather than two states that have to agree.
    requiredOnly: boolean = remindRequiredOnly,
  ) => {
    setSendingReminder(true)
    try {
      // Without a variant the backend auto-picks the next un-sent one and
      // 409s when both have fired; passing it explicitly is how HR re-fires
      // (e.g. extra nudges in the final week).
      const result = await feedbackCampaignsService.sendCampaignReminder(
        campaign.id,
        { ...(variant ? { variant } : {}), requiredOnly },
      )
      const variantLabel =
        result.variant === 'first'
          ? 'First (mid-cycle)'
          : result.variant === 'final'
            ? 'Final (last call)'
            : 'Second (closing week)'
      // Backend dispatches the email fan-out asynchronously so HR doesn't
      // wait on the Graph round-trip; show a "queued" toast instead of
      // a precise sent count.
      toast.success(
        `${variantLabel} reminder queued — ${
          result.recipient_count != null ? `${result.recipient_count} ` : ''
        }${
          result.required_only ? 'permanent / FTC non-responders' : 'non-responders'
        } will be notified shortly`,
      )
      // refresh the drawer so reminder_sent_at timestamps update
      openDrawer(campaign.id)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to send reminder')
    } finally {
      setSendingReminder(false)
    }
  }

  const handleTestLaunch = async () => {
    if (!drawerCampaignId) return
    const ids = testLaunchIds
      .split(/[\n,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    if (ids.length === 0) {
      toast.error('Enter at least one user ID')
      return
    }
    setTestLaunching(true)
    try {
      const summary = await feedbackCampaignsService.testLaunchCampaign(drawerCampaignId, ids)
      toast.success(
        `Test launched — ${summary.recipients_created} recipients (${summary.skipped_no_manager} skipped, no people leader; ${summary.skipped_no_subject} skipped, no subject)`,
      )
      setTestLaunchOpen(false)
      setTestLaunchIds('')
      openDrawer(drawerCampaignId)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Test launch failed')
    } finally {
      setTestLaunching(false)
    }
  }

  // ----------------------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Megaphone className="h-7 w-7 text-blue-600" />
            Annual Upward Feedback Campaigns
          </h2>
          <p className="text-slate-600 mt-1 max-w-3xl">
            Run a bulk, confidential upward-feedback cycle. Respondents are snapshotted
            at launch from the audience filter; subjects (people leaders / CGMs) only
            ever see aggregated counts. Use Test Launch to validate the email +
            questionnaire before going wide.
          </p>
        </div>
        <Button onClick={openCreate} className="shadow-sm whitespace-nowrap">
          <Plus className="h-4 w-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Filter + table */}
      <Card>
        <CardHeader className="border-b border-slate-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Label className="text-sm text-slate-600">Status</Label>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {(Object.keys(STATUS_CONFIG) as CampaignStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_CONFIG[s].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={loadCampaigns}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-3 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading campaigns…
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Megaphone className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-700">No campaigns yet</p>
              <p className="text-sm mt-1">Create one to kick off the upward feedback cycle.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Close at</TableHead>
                  <TableHead className="text-right">Recipients</TableHead>
                  <TableHead className="text-right">Responses</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((c) => {
                  const template = templatesById.get(c.template_id)
                  const responseRate = c.recipient_count
                    ? Math.round((c.response_count / c.recipient_count) * 100)
                    : 0
                  return (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => openDrawer(c.id)}
                    >
                      <TableCell>
                        <div className="font-medium text-slate-900">{c.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {template?.template_name || 'Unknown template'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_CONFIG[c.status].className}>
                          {STATUS_CONFIG[c.status].label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">
                        {RELATIONSHIP_LABEL[c.subject_relationship]}
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">{fmt(c.close_at)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {c.recipient_count}
                        {c.test_recipient_count > 0 && (
                          <span className="text-xs text-slate-400 ml-1">
                            (+{c.test_recipient_count} test)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {c.response_count}{' '}
                        <span className="text-xs text-slate-400">({responseRate}%)</span>
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {c.status === 'draft' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEdit(c)}
                                title="Edit"
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => previewAudience(c.id)}
                                title="Preview audience"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {c.status === 'launched' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEdit(c)}
                                title="Edit dates — extend the close date or change the deadline communicated to staff"
                              >
                                Edit dates
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  setConfirmAction({
                                    kind: 'close',
                                    campaignId: c.id,
                                    campaignName: c.name,
                                  })
                                }
                                title="Close now"
                              >
                                <StopCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {(c.status === 'draft' || c.status === 'scheduled' || c.status === 'launched') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-rose-600 hover:text-rose-700"
                              onClick={() =>
                                setConfirmAction({
                                  kind: 'cancel',
                                  campaignId: c.id,
                                  campaignName: c.name,
                                })
                              }
                              title="Cancel"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {!editingCampaign
                ? 'New campaign'
                : isEditingLaunched
                  ? 'Edit dates'
                  : 'Edit campaign'}
            </DialogTitle>
            <DialogDescription>
              {!editingCampaign
                ? 'Define a draft now; the audience snapshot is taken at launch.'
                : isEditingLaunched
                  ? 'This campaign is live, so the audience and questions are locked — people already have it. Deadlines stay editable.'
                  : 'Drafts and scheduled campaigns can be edited in full.'}
            </DialogDescription>
          </DialogHeader>

          {isEditingLaunched && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
              Changing the deadline also updates the date shown on every
              respondent's card. It does <strong>not</strong> email anyone — use{' '}
              <strong>Re-send closing reminder</strong> if you want to tell people.
            </div>
          )}

          <div className="space-y-4">
            {/* Everything except the deadlines is frozen once launched — the
                audience is snapshotted and respondents already hold the
                survey — so the live-edit dialog shows only what can change. */}
            {!isEditingLaunched && (
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Mid-Year 2026 Upward Feedback"
              />
            </div>
            )}

            {!isEditingLaunched && (
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                rows={2}
                value={form.description || ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional. Shown to admins on the listing."
              />
            </div>
            )}

            {!isEditingLaunched && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template *</Label>
                <Select
                  value={form.template_id}
                  onValueChange={(v) => setForm({ ...form, template_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {annualCampaignTemplates.length === 0 && (
                      <div className="px-3 py-2 text-sm text-slate-500">
                        No annual_campaign templates. Mark one as <code>annual_campaign</code> in
                        Template management first.
                      </div>
                    )}
                    {annualCampaignTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.template_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Subject relationship *</Label>
                <Select
                  value={form.subject_relationship}
                  onValueChange={(v) =>
                    setForm({ ...form, subject_relationship: v as SubjectRelationship })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="line_manager">People leader</SelectItem>
                    <SelectItem value="campus_general_manager">Campus General Manager</SelectItem>
                    <SelectItem value="organisation">Whole organisation (survey)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  <strong>People leader</strong> — each respondent is auto-resolved to their
                  direct people leader. <strong>CGM</strong> — broadcast to all eligible
                  team members; the survey's first question asks which CGM they're giving
                  feedback about (we'll attribute responses to the CGM picked at report time).{' '}
                  <strong>Whole organisation</strong> — broadcast to all eligible team members
                  with no subject at all (org-wide surveys like the Culture Survey); responses
                  stay anonymous and are analysed in aggregate.
                </p>
              </div>
            </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Close at *</Label>
                <Input
                  type="datetime-local"
                  value={form.close_at as string}
                  onChange={(e) => setForm({ ...form, close_at: e.target.value })}
                />
                <p className="text-xs text-slate-500">
                  When the campaign actually closes. Drives reminder timing and
                  the scheduled close.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Deadline to communicate</Label>
                <Input
                  type="datetime-local"
                  value={(form.display_close_at as string) || ''}
                  onChange={(e) => setForm({ ...form, display_close_at: e.target.value })}
                />
                <p className="text-xs text-slate-500">
                  Optional. The date shown in emails and on the respondent's card.
                  Set it <em>earlier</em> than the close date to publish a deadline
                  while quietly staying open for late responses. Leave empty to
                  communicate the real close date.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reminder lead time (days)</Label>
                <Input
                  type="number"
                  min={0}
                  max={60}
                  value={form.reminder_lead_days ?? 7}
                  onChange={(e) =>
                    setForm({ ...form, reminder_lead_days: Number(e.target.value) || 0 })
                  }
                />
                <p className="text-xs text-slate-500">
                  Reminder is sent this many days <em>before</em> close.
                </p>
              </div>
            </div>

            {!isEditingLaunched && (
            <div className="space-y-2">
              <Label>Employment types</Label>
              <div className="flex flex-wrap gap-3">
                {EMPLOYMENT_TYPES.map((t) => {
                  const selected = (form.audience_filter?.employment_types || []).includes(t)
                  return (
                    <label
                      key={t}
                      className="flex items-center gap-2 text-sm border rounded-md px-2.5 py-1.5 hover:bg-slate-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={(checked) => {
                          const cur = form.audience_filter?.employment_types || []
                          const next = checked ? [...cur, t] : cur.filter((x) => x !== t)
                          setForm({
                            ...form,
                            audience_filter: { ...(form.audience_filter as any), employment_types: next },
                          })
                        }}
                      />
                      {t}
                    </label>
                  )
                })}
              </div>
              <p className="text-xs text-slate-500">
                Default <code>PERM + FTC</code>. ICs and casuals are excluded unless explicitly added.
              </p>
            </div>
            )}

            {!isEditingLaunched && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Campuses (optional)</Label>
                <Input
                  value={(form.audience_filter?.campuses || []).join(', ')}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      audience_filter: {
                        ...(form.audience_filter as any),
                        campuses: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                      },
                    })
                  }
                  placeholder="Empty = all campuses"
                />
              </div>
              <div className="space-y-2">
                <Label>Excluded user IDs</Label>
                <Input
                  value={(form.audience_filter?.excluded_user_ids || []).join(', ')}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      audience_filter: {
                        ...(form.audience_filter as any),
                        excluded_user_ids: e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean),
                      },
                    })
                  }
                  placeholder="Comma-separated team member IDs"
                />
              </div>
            </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editingCampaign ? 'Save changes' : 'Create draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail drawer */}
      <Sheet open={!!drawerCampaignId} onOpenChange={(open) => !open && closeDrawer()}>
        <SheetContent className="sm:max-w-2xl w-full overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-blue-600" />
              {drawerCampaign?.name || 'Campaign'}
            </SheetTitle>
            <SheetDescription>
              {drawerCampaign
                ? `${RELATIONSHIP_LABEL[drawerCampaign.subject_relationship]} · closes ${fmt(
                    drawerCampaign.close_at,
                    true,
                  )}`
                : 'Loading campaign details…'}
            </SheetDescription>
            {/* When HR publishes an earlier deadline than the real close,
                say so up front — otherwise the drawer's close date silently
                contradicts every email that went out. */}
            {drawerCampaign?.display_close_at && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Staff were told this closes{' '}
                <strong>{fmt(drawerCampaign.display_close_at, true)}</strong> — it actually
                stays open until <strong>{fmt(drawerCampaign.close_at, true)}</strong> for
                late responses.
              </div>
            )}
            {drawerCampaign && (
              <div className="pt-1">
                <Badge className={STATUS_CONFIG[drawerCampaign.status].className}>
                  {STATUS_CONFIG[drawerCampaign.status].label}
                </Badge>
              </div>
            )}
          </SheetHeader>

          {drawerLoading || !drawerCampaign ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-6 mt-4">
              {/* Tabs */}
              <div className="flex border-b">
                {(['overview', 'progress', 'recipients'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setDrawerTab(t)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                      drawerTab === t
                        ? 'border-blue-600 text-blue-700'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {t === 'overview' ? 'Overview' : t === 'progress' ? 'Progress' : `Recipients (${drawerRecipients.length})`}
                  </button>
                ))}
              </div>

              {/* Overview */}
              {drawerTab === 'overview' && (
                <div className="space-y-4">
                  {drawerCampaign.description && (
                    <p className="text-sm text-slate-600">{drawerCampaign.description}</p>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <InfoCard icon={<Users className="h-4 w-4" />} label="Recipients">
                      {drawerCampaign.recipient_count}
                      {drawerCampaign.test_recipient_count > 0 && (
                        <span className="text-xs text-slate-400 ml-1">
                          (+{drawerCampaign.test_recipient_count} test)
                        </span>
                      )}
                    </InfoCard>
                    <InfoCard icon={<ClipboardList className="h-4 w-4" />} label="Responses">
                      {drawerCampaign.response_count}
                    </InfoCard>
                    <InfoCard icon={<CalendarClock className="h-4 w-4" />} label="Launched at">
                      {fmt(drawerCampaign.launched_at, true)}
                    </InfoCard>
                    <InfoCard icon={<Mail className="h-4 w-4" />} label="Reminder fired">
                      {fmt(drawerCampaign.reminder_sent_at, true)}
                    </InfoCard>
                  </div>

                  {/* Delivered-vs-total chip — surfaces partial-failure
                      states so HR can see eg 1247 / 1300 and hit the
                      Retry button (rendered down in the action row) to
                      resend only the undelivered 53. */}
                  {(() => {
                    const total = drawerCampaign.recipient_count + drawerCampaign.test_recipient_count
                    const delivered = drawerCampaign.invitations_delivered_count
                    if (total === 0 || !drawerCampaign.launched_at) return null
                    const undelivered = Math.max(0, total - delivered)
                    const ok = undelivered === 0
                    return (
                      <div
                        className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-300 bg-amber-50 text-amber-900'}`}
                      >
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          <span>
                            <strong>{delivered}</strong> / {total} invite{total === 1 ? '' : 's'} delivered
                            {undelivered > 0 && (
                              <span className="ml-2 text-xs">
                                ({undelivered} pending re-send)
                              </span>
                            )}
                          </span>
                        </div>
                        {undelivered > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRetryUndelivered(drawerCampaign)}
                            disabled={retryingInvites}
                            title={`Re-dispatch the ${undelivered} undelivered invite${undelivered === 1 ? '' : 's'} — recipients already delivered are skipped automatically.`}
                          >
                            {retryingInvites ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4 mr-2" />
                            )}
                            Retry undelivered
                          </Button>
                        )}
                      </div>
                    )
                  })()}

                  <div className="border rounded-lg p-3 text-sm">
                    <div className="font-medium text-slate-700 mb-2">Audience filter</div>
                    <pre className="bg-slate-50 rounded p-2 text-xs whitespace-pre-wrap">
                      {JSON.stringify(drawerCampaign.audience_filter, null, 2)}
                    </pre>
                  </div>

                  {/* Action row */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    {drawerCampaign.status === 'draft' && (
                      <>
                        <Button onClick={() => previewAudience(drawerCampaign.id)} variant="outline">
                          <Eye className="h-4 w-4 mr-2" /> Preview audience
                        </Button>
                        <Button
                          onClick={() => setTestLaunchOpen(true)}
                          variant="outline"
                        >
                          <TestTube2 className="h-4 w-4 mr-2" /> Test launch
                        </Button>
                        <Button
                          onClick={() =>
                            setConfirmAction({
                              kind: 'launch',
                              campaignId: drawerCampaign.id,
                              campaignName: drawerCampaign.name,
                            })
                          }
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Send className="h-4 w-4 mr-2" /> Launch
                        </Button>
                        {/* Secondary: bundle launch — only useful when another
                            draft campaign exists to pair with. Hides if there's
                            nothing to pair. */}
                        {campaigns.some(
                          (c) => c.id !== drawerCampaign.id && c.status === 'draft',
                        ) && (
                          <Button
                            variant="outline"
                            onClick={() => {
                              // Reset everything so a previous test-mode
                              // session doesn't surprise the user.
                              setBundlePartnerId('')
                              setBundleMode('live')
                              setBundleTestIds('')
                              setBundleOpen(true)
                            }}
                            title="Launch this campaign together with another draft — respondents get one consolidated email. Toggle to Test cohort inside the dialog for a [TEST] dry-run."
                          >
                            <Send className="h-4 w-4 mr-2" /> Launch with…
                          </Button>
                        )}
                      </>
                    )}
                    {drawerCampaign.status === 'launched' && (
                      <>
                        <Button
                          onClick={() => openEdit(drawerCampaign)}
                          variant="outline"
                          title="Extend the close date, or change the deadline communicated to staff"
                        >
                          <CalendarClock className="h-4 w-4 mr-2" />
                          Edit dates
                        </Button>
                        {/* Send reminder — fires the next un-sent reminder
                            (first or second). Once both have fired it becomes
                            an explicit re-send of the closing-week copy, behind
                            a confirm — HR routinely adds extra nudges in the
                            final days. Non-responders only, either way. */}
                        {(() => {
                          const firstSent = !!drawerCampaign.reminder_sent_at
                          const secondSent = !!drawerCampaign.second_reminder_sent_at
                          const next: 'first' | 'second' | 'none' = !firstSent
                            ? 'first'
                            : !secondSent
                              ? 'second'
                              : 'none'
                          const label =
                            next === 'first'
                              ? 'Preview & send first reminder'
                              : next === 'second'
                                ? 'Preview & send second reminder'
                                : 'Preview & re-send closing reminder'
                          const tooltip =
                            next === 'none'
                              ? 'Both scheduled reminders have fired. Opens the preview to re-send the closing-week email to everyone still outstanding.'
                              : `Preview and fire the ${next === 'first' ? 'mid-cycle' : 'closing-week'} reminder — non-responders only.`
                          // Preview must show whatever Send would actually
                          // fire. Once both scheduled reminders have gone the
                          // Send button re-fires the CLOSING copy, so
                          // previewing the mid-cycle one there would show HR
                          // an email nobody is about to receive.
                          const previewVariant: 'first' | 'second' | 'final' =
                            next === 'first' ? 'first' : 'second'
                          return (
                            <>
                              <Button
                                onClick={() =>
                                  handlePreviewReminder(drawerCampaign, previewVariant)
                                }
                                disabled={previewingReminder || sendingReminder}
                                variant="outline"
                                title={tooltip}
                              >
                                {previewingReminder ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Eye className="h-4 w-4 mr-2" />
                                )}
                                {label}
                              </Button>
                              {/* Last-call send. Separate from the scheduled
                                  reminders because it's a judgement call HR
                                  makes in the closing days, not a slot in the
                                  cycle — the countdown is computed at send
                                  time, so it's true whenever it goes out. */}
                              <Button
                                onClick={() =>
                                  handlePreviewReminder(drawerCampaign, 'final')
                                }
                                disabled={sendingReminder || previewingReminder}
                                variant="outline"
                                title="Last-call email with a live countdown to the deadline. Opens the preview so you can check the copy and the audience before it goes."
                              >
                                <BellRing className="h-4 w-4 mr-2" />
                                Send final reminder
                              </Button>
                            </>
                          )
                        })()}
                        <Button
                          onClick={() =>
                            setConfirmAction({
                              kind: 'close',
                              campaignId: drawerCampaign.id,
                              campaignName: drawerCampaign.name,
                            })
                          }
                          variant="outline"
                        >
                          <StopCircle className="h-4 w-4 mr-2" /> Close now
                        </Button>
                      </>
                    )}
                    {/* Export — adaptive label.
                        - draft + test_launched_at  → "Export test responses"
                        - launched / closed / cancelled → "Export responses"
                        - draft, no test            → disabled with tooltip
                       Test cohort responses are exposed unredacted (HR
                       picked the cohort themselves); real exports respect
                       the server-side anonymity threshold (min_n=3). */}
                    {(() => {
                      const isDraft = drawerCampaign.status === 'draft'
                      const hasTest = !!drawerCampaign.test_launched_at
                      const canExport =
                        (isDraft && hasTest) ||
                        drawerCampaign.status === 'launched' ||
                        drawerCampaign.status === 'closed' ||
                        drawerCampaign.status === 'cancelled'
                      const label = isDraft ? 'Export test responses' : 'Export responses'
                      const pdfLabel = isDraft ? 'Generate test PDFs' : 'Generate PDF reports'
                      const tooltip = !canExport
                        ? 'Run a Test Launch first to validate the export, or launch the campaign.'
                        : undefined
                      return (
                        <>
                          <Button
                            variant="outline"
                            onClick={() => handleExport(drawerCampaign.id)}
                            disabled={!canExport || exporting}
                            title={tooltip}
                          >
                            {exporting ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4 mr-2" />
                            )}
                            {label}
                          </Button>
                          {(() => {
                            // Organisation-wide surveys have no per-subject
                            // reports (each "subject" is an anonymous
                            // respondent) — the backend 400s the build, so
                            // don't offer the button at all.
                            if (drawerCampaign.subject_relationship === 'organisation') return null
                            // Job-aware PDF button: building → live progress;
                            // complete (resumed job) → direct download; else →
                            // start a new background build.
                            const building =
                              reportJob != null &&
                              (reportJob.status === 'pending' || reportJob.status === 'running')
                            const readyToDownload =
                              reportJob != null && reportJob.status === 'complete'
                            const buildingLabel =
                              reportJob && reportJob.total_count > 0
                                ? `Building ${reportJob.built_count} of ${reportJob.total_count}…`
                                : 'Starting build…'
                            return (
                              <Button
                                variant="outline"
                                onClick={() =>
                                  readyToDownload
                                    ? downloadReportJobZip(drawerCampaign.id, reportJob)
                                    : handleGenerateReports(drawerCampaign.id)
                                }
                                disabled={!canExport || building}
                                title={
                                  tooltip ||
                                  'AI-synthesised per-subject PDF reports — built in the background, bundled as a ZIP for distribution.'
                                }
                              >
                                {building ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <FileDown className="h-4 w-4 mr-2" />
                                )}
                                {building
                                  ? buildingLabel
                                  : readyToDownload
                                    ? 'Download PDF reports'
                                    : pdfLabel}
                              </Button>
                            )
                          })()}
                        </>
                      )
                    })()}
                    {(drawerCampaign.status === 'draft' ||
                      drawerCampaign.status === 'scheduled' ||
                      drawerCampaign.status === 'launched') && (
                      <Button
                        variant="outline"
                        className="text-rose-700 border-rose-200 hover:bg-rose-50"
                        onClick={() =>
                          setConfirmAction({
                            kind: 'cancel',
                            campaignId: drawerCampaign.id,
                            campaignName: drawerCampaign.name,
                          })
                        }
                      >
                        <XCircle className="h-4 w-4 mr-2" /> Cancel
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Progress */}
              {drawerTab === 'progress' && (
                <div className="space-y-4">
                  {drawerProgress ? (
                    (() => {
                      // Distinguish test vs real respondent counts so a
                      // draft-with-test-launch doesn't read as "1 of 1 (100%)"
                      // and mask the fact that no real recipients exist yet.
                      const isDraft = drawerCampaign.status === 'draft'
                      const testCount = drawerCampaign.test_recipient_count
                      const total = drawerProgress.recipient_count
                      const done = drawerProgress.response_count
                      // While in draft, the only recipients are the test cohort,
                      // so progress IS test progress. Once launched, both real
                      // and test rows are in `total` — show them broken out.
                      const realTotal = Math.max(0, total - testCount)
                      const pct = total ? Math.round((done / total) * 100) : 0

                      // Employment-type split. ICs and casuals are invited to
                      // org-wide surveys but participate voluntarily, so a
                      // single blended rate understates engagement among the
                      // people actually asked to take part. Only lead with the
                      // core figure when the cohort really does contain
                      // voluntary participants — otherwise it's the same
                      // number printed twice.
                      const segments = drawerProgress.segments ?? []
                      const core = drawerProgress.core ?? null
                      const hasVoluntary =
                        !!core && core.recipient_count > 0 && core.recipient_count < realTotal
                      const rate = (r: number, n: number) => (n ? Math.round((r / n) * 100) : 0)
                      const corePct = core ? rate(core.response_count, core.recipient_count) : 0
                      return (
                        <>
                          {isDraft && testCount > 0 && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                              This campaign is still a <strong>draft</strong>. The counts below
                              reflect the test cohort only — no real recipients have been
                              invited yet.
                            </div>
                          )}

                          {hasVoluntary && core && (
                            <div className="rounded-lg border border-heritage-blue/30 bg-heritage-blue/[0.03] p-4">
                              <div className="flex items-baseline justify-between">
                                <div className="text-sm font-medium text-slate-700">
                                  {core.label}
                                  <span className="ml-2 text-xs font-normal text-slate-500">
                                    required participants
                                  </span>
                                </div>
                                <div className="text-2xl font-bold text-heritage-blue tabular-nums">
                                  {corePct}%
                                </div>
                              </div>
                              <Progress value={corePct} className="mt-2" />
                              <div className="text-xs text-slate-500 mt-2">
                                {core.response_count} of {core.recipient_count} responded ·{' '}
                                {core.recipient_count - core.response_count} outstanding
                              </div>
                            </div>
                          )}

                          <div className="rounded-lg border p-4">
                            <div className="flex items-baseline justify-between">
                              <div className="text-sm text-slate-600">
                                {isDraft
                                  ? 'Test response rate'
                                  : hasVoluntary
                                    ? 'All invited (including voluntary)'
                                    : 'Overall response rate'}
                              </div>
                              <div
                                className={
                                  hasVoluntary
                                    ? 'text-lg font-semibold text-slate-600 tabular-nums'
                                    : 'text-2xl font-bold text-slate-900 tabular-nums'
                                }
                              >
                                {pct}%
                              </div>
                            </div>
                            <Progress value={pct} className="mt-2" />
                            <div className="text-xs text-slate-500 mt-2">
                              {done} of {total} responded · {total - done} outstanding
                            </div>
                            {!isDraft && testCount > 0 && (
                              <div className="text-xs text-slate-400 mt-2">
                                Includes {testCount} test recipient{testCount === 1 ? '' : 's'} (real cohort: {realTotal})
                              </div>
                            )}
                          </div>

                          {segments.length > 1 && (
                            <div className="rounded-lg border overflow-hidden">
                              <div className="px-4 py-2.5 border-b bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
                                By employment type
                              </div>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-xs text-slate-500">
                                    <th className="text-left font-medium px-4 py-2">Employment type</th>
                                    <th className="text-right font-medium px-4 py-2">Invited</th>
                                    <th className="text-right font-medium px-4 py-2">Responded</th>
                                    <th className="text-right font-medium px-4 py-2">Rate</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {segments.map((s) => (
                                    <tr key={s.key} className="border-t">
                                      <td className="px-4 py-2 text-slate-700">
                                        {s.label}
                                        {!core?.keys.includes(s.key) && (
                                          <span className="ml-2 text-xs text-slate-400">voluntary</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                                        {s.recipient_count}
                                      </td>
                                      <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                                        {s.response_count}
                                      </td>
                                      <td className="px-4 py-2 text-right tabular-nums font-medium text-slate-900">
                                        {rate(s.response_count, s.recipient_count)}%
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <div className="px-4 py-2 border-t bg-slate-50/60 text-xs text-slate-400">
                                Test recipients excluded. Employment type is read from the live
                                directory, so it reflects any changes since launch.
                              </div>
                            </div>
                          )}

                          <p className="text-xs text-slate-500">
                            Per-respondent identities and answers are deliberately not shown —
                            campaign responses are confidential to the subject. Use the{' '}
                            <strong>Export</strong> button on the Overview tab to download the
                            anonymised workbook (respondents shown as R-001, R-002, …).
                          </p>
                        </>
                      )
                    })()
                  ) : (
                    <p className="text-sm text-slate-500">Progress not available yet.</p>
                  )}
                </div>
              )}

              {/* Recipients */}
              {drawerTab === 'recipients' && (
                <div>
                  <ScrollArea className="h-[500px] border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Respondent</TableHead>
                          <TableHead>Subject</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Invited</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {drawerRecipients.map((r) => (
                          <TableRow key={`${r.campaign_id}-${r.respondent_user_id}`}>
                            <TableCell className="text-sm">
                              <div className="font-medium">
                                {r.respondent_user_id}
                                {r.is_test && (
                                  <Badge className="ml-2 bg-amber-100 text-amber-700">TEST</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{r.subject_user_id}</TableCell>
                            <TableCell className="text-sm">
                              {r.has_response ? (
                                <Badge className="bg-emerald-100 text-emerald-700">Responded</Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-600">Pending</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-slate-500">
                              {fmt(r.invitation_sent_at, true)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {drawerRecipients.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-10 text-slate-400">
                              Audience hasn't been snapshotted yet — launch (or test-launch) to populate.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Audience preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Audience preview</DialogTitle>
            <DialogDescription>
              Cohort that will receive the campaign at launch. Snapshot is taken at launch time —
              re-preview after editing the filter.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : preview ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <InfoCard icon={<CheckCircle2 className="h-4 w-4" />} label="Eligible" emphasis>
                  {preview.eligible_count}
                </InfoCard>
                <InfoCard icon={<Users className="h-4 w-4" />} label="Total potential">
                  {preview.total_potential}
                </InfoCard>
                <InfoCard icon={<AlertCircle className="h-4 w-4" />} label="No people leader">
                  {preview.excluded_no_manager}
                </InfoCard>
                <InfoCard icon={<AlertCircle className="h-4 w-4" />} label="No subject">
                  {preview.excluded_no_subject}
                </InfoCard>
              </div>

              <div>
                <div className="text-sm font-medium text-slate-700 mb-2">
                  Sample (first {preview.sample.length})
                </div>
                <ScrollArea className="h-72 border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Respondent</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Position</TableHead>
                        <TableHead>Campus</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.sample.map((s, i) => (
                        <TableRow key={`${s.respondent_user_id}-${i}`}>
                          <TableCell className="text-sm">
                            <div className="font-medium">
                              {s.respondent_display_name || s.respondent_user_id}
                            </div>
                            <div className="text-xs text-slate-500">{s.respondent_user_id}</div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {s.subject_display_name || s.subject_user_id || '—'}
                          </TableCell>
                          <TableCell className="text-xs text-slate-600">
                            {s.respondent_position_type || '—'}
                          </TableCell>
                          <TableCell className="text-xs text-slate-600">
                            {s.respondent_campus || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bundle launch dialog ("Launch with…") — also covers test launches.
          A live/test toggle at the top of the dialog routes the action to
          either /campaign-bundles/launch or /campaign-bundles/test-launch.
          In test mode HR enters a cohort of user IDs and only those people
          receive the consolidated [TEST] invite covering both campaigns. */}
      <Dialog open={bundleOpen} onOpenChange={setBundleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {bundleMode === 'test' ? 'Test launch as a bundle' : 'Launch as a bundle'}
            </DialogTitle>
            <DialogDescription>
              {bundleMode === 'test' ? (
                <>
                  Run a consolidated [TEST] launch of <strong>{drawerCampaign?.name}</strong>{' '}
                  together with another draft. The selected user IDs receive a single
                  invite email covering both surveys — useful for validating the
                  end-to-end experience before going wide.
                </>
              ) : (
                <>
                  Launch <strong>{drawerCampaign?.name}</strong> together with another draft
                  campaign. Each campaign keeps its own audience, template, and close
                  date — but respondents who qualify for both receive a single
                  consolidated invite email (and a single reminder when due).
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Live / Test mode toggle */}
          <div className="flex gap-2 rounded-md border border-slate-200 p-1 mb-1">
            {(['live', 'test'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setBundleMode(mode)}
                disabled={bundleLaunching}
                className={[
                  'flex-1 rounded px-3 py-1.5 text-sm font-medium transition-colors',
                  bundleMode === mode
                    ? 'bg-heritage-blue text-white'
                    : 'text-slate-600 hover:bg-slate-50',
                ].join(' ')}
              >
                {mode === 'live' ? 'Live launch' : 'Test cohort'}
              </button>
            ))}
          </div>

          <div className="space-y-3">
            <Label>Pair with</Label>
            <Select value={bundlePartnerId} onValueChange={setBundlePartnerId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a draft campaign" />
              </SelectTrigger>
              <SelectContent>
                {campaigns
                  .filter(
                    (c) =>
                      c.id !== drawerCampaignId &&
                      // Live mode requires both to be draft (per backend guard).
                      // Test mode allows non-closed/cancelled siblings — but most
                      // practical use is draft+draft so we keep it consistent.
                      c.status === 'draft',
                  )
                  .map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              Both campaigns must be in <strong>Draft</strong>. After bundle launch
              they're independently active and each can close, get reports exported,
              or be cancelled on its own.
            </p>

            {bundleMode === 'test' && (
              <div className="space-y-2 pt-2">
                <Label>Test respondent user IDs</Label>
                <Textarea
                  rows={4}
                  value={bundleTestIds}
                  onChange={(e) => setBundleTestIds(e.target.value)}
                  placeholder="One per line, or comma-separated. e.g.&#10;43xxxxx&#10;43yyyyy"
                  disabled={bundleLaunching}
                />
                <p className="text-xs text-slate-500">
                  Each user needs a resolvable subject for the people leader
                  campaign (CGM uses self-placeholder, so any active team member works
                  there). Test recipients are stored on each campaign so the real
                  launch later excludes them automatically.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBundleOpen(false)}
              disabled={bundleLaunching}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLaunchBundle}
              disabled={
                bundleLaunching ||
                !bundlePartnerId ||
                (bundleMode === 'test' && !bundleTestIds.trim())
              }
              className={
                bundleMode === 'test'
                  ? ''
                  : 'bg-blue-600 hover:bg-blue-700'
              }
              variant={bundleMode === 'test' ? 'outline' : 'default'}
            >
              {bundleLaunching ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : bundleMode === 'test' ? (
                <TestTube2 className="h-4 w-4 mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {bundleMode === 'test' ? 'Send test' : 'Launch both'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder preview dialog — shows the exact subject + HTML body
          HR would dispatch. Renders inside an iframe so Outlook-safe
          inline styles render faithfully (no parent-page CSS bleed). */}
      <Dialog
        open={!!reminderPreview}
        onOpenChange={(open) => !open && setReminderPreview(null)}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {reminderPreview?.variant === 'final'
                ? 'Preview — final (last-call) reminder'
                : reminderPreview?.variant === 'second'
                  ? 'Preview — second (closing-week) reminder'
                  : 'Preview — first (mid-cycle) reminder'}
            </DialogTitle>
            <DialogDescription>
              Rendered exactly as recipients will see it. Bundle linkage
              {reminderPreview && reminderPreview.campaigns_in_bundle.length > 1
                ? ` includes ${reminderPreview.campaigns_in_bundle.length} linked campaigns — one button per survey.`
                : ' — solo campaign, single button.'}
              {' '}Closes <strong>{reminderPreview?.close_str}</strong>.
            </DialogDescription>
          </DialogHeader>
          {reminderPreview && (
            <>
              <div className="flex-shrink-0 rounded-md border bg-slate-50 px-3 py-2 text-sm">
                <span className="text-slate-500">Subject:</span>{' '}
                <span className="font-medium">{reminderPreview.subject}</span>
              </div>

              {/* Who this actually goes to. A reminder is a four-figure mailout;
                  firing it without seeing the audience is the mistake worth
                  designing out. Counts come from the same resolver the send
                  uses, so what HR approves is what gets mailed. */}
              <div className="flex-shrink-0 rounded-md border bg-white px-3 py-2 text-sm space-y-2">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-slate-500">Will be emailed</span>
                  {previewingReminder ? (
                    <span className="text-sm text-slate-500 italic">recalculating…</span>
                  ) : (
                    <span className="text-lg font-semibold tabular-nums">
                      {reminderPreview.audience.recipient_count.toLocaleString()}
                    </span>
                  )}
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={remindRequiredOnly}
                    disabled={previewingReminder}
                    onChange={(e) => {
                      const next = e.target.checked
                      setRemindRequiredOnly(next)
                      // Pass `next` explicitly — reading state here returns the
                      // pre-tick value and the count silently stays wrong.
                      if (drawerCampaign) {
                        handlePreviewReminder(
                          drawerCampaign, reminderPreview.variant, next)
                      }
                    }}
                  />
                  <span>
                    <span className="font-medium">
                      Required participants only (permanent &amp; fixed-term)
                    </span>
                    <span className="block text-xs text-slate-500">
                      Skips independent contractors and casuals, who were told
                      participation is voluntary. Anyone whose employment type is
                      unknown is skipped too.
                    </span>
                  </span>
                </label>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  {reminderPreview.audience.outstanding_by_employment_type.map((s2) => (
                    <span key={s2.label}>
                      {s2.label}: <span className="tabular-nums">{s2.count.toLocaleString()}</span>
                    </span>
                  ))}
                </div>
                {reminderPreview.audience.suppressed > 0 ? (
                  <div className="text-xs text-amber-700">
                    {reminderPreview.audience.suppressed.toLocaleString()} of{' '}
                    {reminderPreview.audience.outstanding_total.toLocaleString()} outstanding
                    will NOT be emailed.
                  </div>
                ) : (
                  <div className="text-xs text-slate-500">
                    All {reminderPreview.audience.outstanding_total.toLocaleString()} outstanding
                    recipients will be emailed.
                  </div>
                )}
              </div>
              <iframe
                title="Reminder email preview"
                srcDoc={reminderPreview.body_html}
                className="flex-1 min-h-[500px] w-full rounded-md border bg-white"
                sandbox=""
              />
              <DialogFooter className="flex-shrink-0">
                <Button
                  variant="outline"
                  onClick={() => setReminderPreview(null)}
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    if (!drawerCampaign) return
                    // Pass the variant that was actually previewed —
                    // otherwise the backend re-picks the next un-sent one and
                    // HR sends different copy than the one they just read
                    // (or gets a 409 once both have fired).
                    const previewed = reminderPreview.variant
                    // ...and the filter the SERVER applied to the count on this
                    // button, so the figure approved is the figure dispatched.
                    const audienceFilter = reminderPreview.audience.required_only
                    setReminderPreview(null)
                    handleSendReminderNow(drawerCampaign, previewed, audienceFilter)
                  }}
                  disabled={
                    sendingReminder ||
                    previewingReminder ||
                    // The count on this button belongs to a different filter
                    // than the checkbox shows. Refuse rather than send an
                    // audience nobody approved.
                    remindRequiredOnly !== reminderPreview.audience.required_only
                  }
                  title="Fire the reminder you just previewed to the audience shown above."
                >
                  {sendingReminder ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Bell className="h-4 w-4 mr-2" />
                  )}
                  {previewingReminder
                    ? 'Recalculating…'
                    : `Send to ${reminderPreview.audience.recipient_count.toLocaleString()} now`}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Test launch dialog */}
      <Dialog open={testLaunchOpen} onOpenChange={setTestLaunchOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test launch</DialogTitle>
            <DialogDescription>
              Sends the campaign to specific user IDs only — bypasses the audience filter. Use this
              to validate the email + survey before going wide. Real launch later excludes test
              recipients automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Respondent user IDs</Label>
            <Textarea
              rows={5}
              value={testLaunchIds}
              onChange={(e) => setTestLaunchIds(e.target.value)}
              placeholder="One per line, or comma-separated. e.g.&#10;43xxxxx&#10;43yyyyy"
            />
            <p className="text-xs text-slate-500">
              {drawerCampaign?.subject_relationship === 'organisation' ||
              drawerCampaign?.subject_relationship === 'campus_general_manager' ? (
                <>Broadcast campaign — any active user with an employee ID can receive a test.</>
              ) : (
                <>
                  Each user must have a resolvable{' '}
                  {drawerCampaign && RELATIONSHIP_LABEL[drawerCampaign.subject_relationship].toLowerCase()}{' '}
                  — they'll be skipped otherwise.
                </>
              )}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestLaunchOpen(false)} disabled={testLaunching}>
              Cancel
            </Button>
            <Button onClick={handleTestLaunch} disabled={testLaunching}>
              {testLaunching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TestTube2 className="h-4 w-4 mr-2" />}
              Send test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm dialog (launch/close/cancel) */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.kind === 'launch' && 'Launch campaign?'}
              {confirmAction?.kind === 'close' && 'Close campaign?'}
              {confirmAction?.kind === 'cancel' && 'Cancel campaign?'}
              {confirmAction?.kind === 'resend-reminder' &&
                (confirmAction.variant === 'final'
                  ? 'Send the final reminder?'
                  : 'Send another reminder?')}
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.kind === 'launch' && (
                <>
                  This snapshots the audience and emails every eligible respondent immediately for{' '}
                  <strong>{confirmAction.campaignName}</strong>. There is no undo other than cancel.
                </>
              )}
              {confirmAction?.kind === 'close' && (
                <>
                  Submissions for <strong>{confirmAction.campaignName}</strong> will be hard-rejected
                  from now on. Existing responses remain.
                </>
              )}
              {confirmAction?.kind === 'resend-reminder' && confirmAction.variant === 'final' && (
                <>
                  Sends the last-call email for <strong>{confirmAction.campaignName}</strong> to{' '}
                  <span className="font-medium">everyone who hasn't responded yet</span> — people
                  who already submitted are never re-mailed. The countdown ("Just 2 days to go")
                  is worked out when you send, against the deadline staff were told, so it's
                  accurate whenever it goes out.
                </>
              )}
              {confirmAction?.kind === 'resend-reminder' && confirmAction.variant !== 'final' && (
                <>
                  Both scheduled reminders for <strong>{confirmAction.campaignName}</strong> have
                  already fired. This sends the closing-week email again to{' '}
                  <span className="font-medium">everyone who hasn't responded yet</span> — people
                  who already submitted are never re-mailed.
                </>
              )}
              {confirmAction?.kind === 'cancel' && (
                <>
                  Cancels <strong>{confirmAction?.campaignName}</strong>.
                  {' '}
                  <span className="font-medium">If no responses have been submitted yet</span>,
                  all pending invitations are removed from respondents' inboxes — the campaign
                  effectively "never happened." If responses already exist, they're preserved
                  for the audit trail and the campaign is just flagged cancelled (no more
                  invites or reminders).
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)} disabled={confirmBusy}>
              Back
            </Button>
            {/* Reminder sends are mass emails — let HR read the actual
                rendered copy without losing their place. */}
            {confirmAction?.kind === 'resend-reminder' && (
              <Button
                variant="outline"
                disabled={confirmBusy || previewingReminder}
                onClick={() => {
                  const { campaignId, variant } = confirmAction
                  const campaign =
                    drawerCampaign?.id === campaignId
                      ? drawerCampaign
                      : campaigns.find((c) => c.id === campaignId)
                  setConfirmAction(null)
                  if (campaign) handlePreviewReminder(campaign, variant)
                }}
              >
                {previewingReminder ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="h-4 w-4 mr-2" />
                )}
                Preview it first
              </Button>
            )}
            <Button
              onClick={performAction}
              disabled={confirmBusy}
              className={
                confirmAction?.kind === 'cancel'
                  ? 'bg-rose-600 hover:bg-rose-700'
                  : confirmAction?.kind === 'launch'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : ''
              }
            >
              {confirmBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ----------------------------------------------------------------------------
// Local InfoCard helper — keeps the drawer/preview tiles consistent without
// pulling another component.
// ----------------------------------------------------------------------------

const InfoCard: React.FC<{
  icon: React.ReactNode
  label: string
  emphasis?: boolean
  children: React.ReactNode
}> = ({ icon, label, emphasis, children }) => (
  <div
    className={`rounded-lg border p-3 ${
      emphasis ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-white'
    }`}
  >
    <div className="flex items-center gap-2 text-xs text-slate-500">
      {icon}
      <span>{label}</span>
    </div>
    <div className="text-xl font-semibold text-slate-900 mt-1 tabular-nums">{children}</div>
  </div>
)

export default FeedbackCampaignsPage
